if (!customElements.get('secure-checkout')) {
  customElements.define(
    'secure-checkout',
    class SecureCheckout extends HTMLElement {
      constructor() {
        super();

        this.variantId = Number(this.dataset.variantId) || 0;
        this.formId = this.dataset.formId;
        this.protectionInCart = this.dataset.protectionInCart === 'true';
        this.protectionQuantity = Number(this.dataset.protectionQuantity) || 0;
        this.autoInclude = this.dataset.autoInclude === 'true';
        this.removeOrphanProtection = this.dataset.removeOrphanProtection === 'true';

        this.secureButton = this.querySelector('[data-secure-checkout]');
        this.plainButton = this.querySelector('[data-plain-checkout]');
        this.switchButton = this.querySelector('[data-protection-switch]');
        this.ctaLabel = this.querySelector('[data-secure-label]');
        this.ctaLock = this.querySelector('.sb-secure__cta-lock');
        this.infoButton = this.querySelector('[data-protection-info]');
        this.infoPanel = this.querySelector('[data-protection-panel]');
        this.infoCloseButton = this.querySelector('[data-protection-close]');
        this.errorElement = this.querySelector('[data-secure-error]');

        this.onSecureClick = this.onSecureClick.bind(this);
        this.onPlainClick = this.onPlainClick.bind(this);
        this.onSwitchClick = this.onSwitchClick.bind(this);
        this.onInfoClick = this.onInfoClick.bind(this);
        this.onInfoCloseClick = this.onInfoCloseClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
      }

      connectedCallback() {
        this.secureButton?.addEventListener('click', this.onSecureClick);
        this.plainButton?.addEventListener('click', this.onPlainClick);
        this.switchButton?.addEventListener('click', this.onSwitchClick);
        this.infoButton?.addEventListener('click', this.onInfoClick);
        this.infoCloseButton?.addEventListener('click', this.onInfoCloseClick);
        this.addEventListener('keydown', this.onKeyDown);

        // New markup can connect while a protection update is rendering.
        // It must not start another auto-add or enable checkout mid-update.
        if (window.sbProtectionCartMutation) {
          this.setLoading(true, true);
          return;
        }

        if (this.removeOrphanProtection && !window.sbProtectionCleanupStarted) {
          window.sbProtectionCleanupStarted = true;
          this.removeProtectionAndRefresh();
        } else if (this.protectionQuantity > 1 && !window.sbProtectionQuantityRepairStarted) {
          window.sbProtectionQuantityRepairStarted = true;
          this.normalizeProtectionAndRefresh();
        } else if (
          this.autoInclude &&
          !this.protectionInCart &&
          !window.sbProtectionAutoIncludeStarted &&
          !SecureCheckout.hasDeclined()
        ) {
          // Protection is part of the price by default, so a cart that is
          // missing the line gets it before the shopper reads a total that
          // would otherwise be short by the protection fee.
          window.sbProtectionAutoIncludeStarted = true;
          this.includeProtection();
        }
      }

      /* ---- Session memory of an explicit opt-out -------------------------- */

      static get DECLINED_KEY() {
        return 'sb:protection-declined';
      }

      static hasDeclined() {
        try {
          return window.sessionStorage.getItem(SecureCheckout.DECLINED_KEY) === '1';
        } catch (error) {
          return false;
        }
      }

      static setDeclined(declined) {
        try {
          if (declined) window.sessionStorage.setItem(SecureCheckout.DECLINED_KEY, '1');
          else window.sessionStorage.removeItem(SecureCheckout.DECLINED_KEY);
        } catch (error) {
          /* Private browsing: fall back to auto-including, never to erroring. */
        }
      }

      disconnectedCallback() {
        this.secureButton?.removeEventListener('click', this.onSecureClick);
        this.plainButton?.removeEventListener('click', this.onPlainClick);
        this.switchButton?.removeEventListener('click', this.onSwitchClick);
        this.infoButton?.removeEventListener('click', this.onInfoClick);
        this.infoCloseButton?.removeEventListener('click', this.onInfoCloseClick);
        this.removeEventListener('keydown', this.onKeyDown);
      }

      /* ---- Protection details disclosure --------------------------------- */

      onInfoClick(event) {
        event.preventDefault();
        this.setInfoOpen(this.infoPanel?.hidden);
      }

      onInfoCloseClick(event) {
        event.preventDefault();
        this.setInfoOpen(false);
        this.infoButton?.focus();
      }

      onKeyDown(event) {
        if (event.key !== 'Escape' || this.infoPanel?.hidden) return;

        event.stopPropagation();
        this.setInfoOpen(false);
        this.infoButton?.focus();
      }

      setInfoOpen(isOpen) {
        if (!this.infoButton || !this.infoPanel) return;

        this.infoButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        this.infoPanel.hidden = !isOpen;
      }

      /* ---- Toggle: protection on / off ----------------------------------- */

      get switchOn() {
        if (this.switchButton) return this.switchButton.getAttribute('aria-checked') === 'true';
        return this.protectionInCart;
      }

      syncSwitchState(isOn) {
        this.classList.toggle('is-secured', isOn);
        this.switchButton?.setAttribute('aria-checked', isOn ? 'true' : 'false');
        if (this.ctaLabel) {
          const label = isOn ? this.ctaLabel.dataset.labelSecure : this.ctaLabel.dataset.labelPlain;
          if (label) this.ctaLabel.textContent = label;
        }
        if (this.ctaLock) this.ctaLock.hidden = !isOn;
      }

      onSwitchClick(event) {
        event.preventDefault();
        this.toggleProtection(!this.switchOn);
      }

      async toggleProtection(turnOn) {
        if (this.isLoading || window.sbProtectionCartMutation || !this.variantId) return;

        this.clearError();
        const wasDeclined = SecureCheckout.hasDeclined();
        if (document.activeElement === this.switchButton) this.focusedProtectionForm = this.formId;
        this.forEachSurface((surface) => surface.syncSwitchState(turnOn));
        this.setCartLoading(true);

        try {
          SecureCheckout.setDeclined(!turnOn);
          await this.mutateProtectionCart(turnOn ? 'ensure' : 'remove', true);
        } catch (error) {
          console.error(error);
          SecureCheckout.setDeclined(wasDeclined);
          this.forEachSurface((surface) => {
            surface.syncSwitchState(surface.protectionInCart);
            surface.displayError();
          });
        } finally {
          this.setCartLoading(false);
        }
      }

      /* ---- Primary path: add protection, then checkout -------------------- */

      onSecureClick(event) {
        // Stop the native form submit so we can add protection first.
        event.preventDefault();
        if (this.switchButton && !this.switchOn) {
          this.proceedWithoutProtection();
          return;
        }
        this.secureAndCheckout();
      }

      async secureAndCheckout() {
        if (this.isLoading) return;

        if (!this.variantId) {
          this.continueCheckout();
          return;
        }

        this.clearError();
        this.setLoading(true);

        try {
          // Choosing protection clears any earlier refusal in this session.
          SecureCheckout.setDeclined(false);
          await this.mutateProtectionCart('ensure');
          this.protectionInCart = true;
          this.protectionQuantity = 1;
          this.continueCheckout();
        } catch (error) {
          console.error(error);
          this.setLoading(false);
          this.displayError();
        }
      }

      /* ---- Secondary path: checkout without protection (no confirmation) -- */

      onPlainClick(event) {
        event.preventDefault();
        this.proceedWithoutProtection();
      }

      async proceedWithoutProtection() {
        if (this.isLoading) return;

        if (!this.variantId) {
          this.continueCheckout();
          return;
        }

        this.clearError();
        this.setLoading(true);

        try {
          // Remember the refusal for the rest of the session, so the
          // auto-include above never quietly puts it back.
          SecureCheckout.setDeclined(true);
          await this.removeProtection();
          this.continueCheckout();
        } catch (error) {
          console.error(error);
          this.setLoading(false);
          this.displayError();
        }
      }

      /**
       * Put protection into a cart that is missing it, then re-render the cart
       * surfaces so the total on screen is the total at checkout.
       *
       * Deliberately quiet: no spinner and no error message. This is not
       * something the shopper asked for, so if it fails the cart simply stays
       * as it was and the "Protected checkout" button adds protection on click
       * exactly as it always has.
       */
      async includeProtection() {
        if (this.isLoading || !this.variantId) return;

        this.setCartLoading(true);
        try {
          await this.mutateProtectionCart('ensure', true);
        } catch (error) {
          console.error(error);
        } finally {
          // Re-arm. The flag only exists to stop the drawer and the cart page
          // firing at the same moment; holding it for the life of the page
          // would mean a cart that is emptied (protection removed as an
          // orphan) and then refilled never gets protection back.
          window.sbProtectionAutoIncludeStarted = false;
          this.setCartLoading(false);
        }
      }

      getCartSurfaces() {
        const surfaces = [
          { section: 'cart-drawer', target: '#CartDrawer .drawer__inner', source: '.drawer__inner' },
          { section: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' },
        ];
        for (const [id, selector] of [['main-cart-items', '.js-contents'], ['main-cart-footer', '.cart__blocks']]) {
          const container = document.getElementById(id);
          if (container) surfaces.push({ section: container.dataset.id, target: `#${id} ${selector}`, source: selector });
        }
        return surfaces.filter((surface) => surface.section && document.querySelector(surface.target));
      }

      // The mutation already returns Shopify-rendered prices, discounts and
      // ladder HTML. Update both surfaces together, without a document reload.
      async refreshCartSurfaces(cart) {
        const surfaces = this.getCartSurfaces();
        let sections = cart.sections || {};
        const parse = () => surfaces.map((surface) => ({
          target: document.querySelector(surface.target),
          source: typeof sections[surface.section] === 'string'
            ? new DOMParser().parseFromString(sections[surface.section], 'text/html').querySelector(surface.source)
            : null,
        }));
        let replacements = parse();
        if (replacements.some(({ source }) => !source)) {
          // A mutation can succeed while a bundled section fails. Retry only
          // the rendering; never repeat an add or silently leave stale totals.
          const url = new URL(window.routes.cart_url, window.location.origin);
          url.search = window.location.search;
          url.searchParams.set('sections', surfaces.map((surface) => surface.section).join(','));
          try {
            const response = await fetch(url, { signal: window.AbortSignal?.timeout?.(15000) });
            if (response.ok) {
              sections = await response.json();
              replacements = parse();
            }
          } catch (error) {
            console.error(error);
          }
          if (replacements.some(({ source }) => !source)) {
            window.location.reload();
            return;
          }
        }

        const active = document.activeElement;
        const focusedForm = active?.matches('[data-protection-switch]') ? active.closest('secure-checkout')?.formId : null;
        const drawerItems = document.querySelector('#CartDrawer .drawer__contents');
        const drawerScroll = drawerItems?.scrollTop;
        replacements.forEach(({ target, source }) => { target.innerHTML = source.innerHTML; });
        document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach((element) =>
          element.classList.toggle('is-empty', cart.item_count === 0)
        );
        const bubble = document.getElementById('cart-icon-bubble');
        bubble?.classList.toggle('site-actions__cart--empty', !bubble.querySelector('.site-actions__cart-count'));
        const freshDrawerItems = document.querySelector('#CartDrawer .drawer__contents');
        if (freshDrawerItems && drawerScroll !== undefined) freshDrawerItems.scrollTop = drawerScroll;
        // Restored once the new controls are enabled in setCartLoading(false).
        this.focusedProtectionForm = focusedForm || this.focusedProtectionForm;
      }

      async removeProtectionAndRefresh() {
        if (this.isLoading || !this.variantId) return;

        this.clearError();
        this.setCartLoading(true);

        try {
          await this.mutateProtectionCart('remove', true);
        } catch (error) {
          console.error(error);
          this.displayError();
        } finally {
          window.sbProtectionCleanupStarted = false;
          this.setCartLoading(false);
        }
      }

      async removeProtection() {
        const parsedState = await this.mutateProtectionCart('remove');

        this.protectionInCart = false;
        this.protectionQuantity = 0;
        return parsedState;
      }

      async normalizeProtectionAndRefresh() {
        if (this.isLoading || !this.variantId) return;

        this.clearError();
        this.setCartLoading(true);

        try {
          await this.mutateProtectionCart('cap', true);
        } catch (error) {
          console.error(error);
          this.displayError();
        } finally {
          window.sbProtectionQuantityRepairStarted = false;
          this.setCartLoading(false);
        }
      }

      /**
       * Serialize protection mutations across the cart page and drawer. Both
       * components can exist in the DOM at once, so a per-element loading flag
       * is not enough to prevent two simultaneous adds.
       */
      mutateProtectionCart(mode, refresh = false) {
        const previousMutation = window.sbProtectionCartMutation || Promise.resolve();
        const currentMutation = previousMutation
          .catch(() => {})
          .then(async () => {
            const cart = await this.applyProtectionCartRule(mode, refresh);
            if (refresh) await this.refreshCartSurfaces(cart);
            return cart;
          });

        window.sbProtectionCartMutation = currentMutation;

        return currentMutation.finally(() => {
          if (window.sbProtectionCartMutation === currentMutation) {
            window.sbProtectionCartMutation = null;
          }
        });
      }

      async applyProtectionCartRule(mode, refresh) {
        // Absolute quantities are idempotent across page/drawer and other tabs.
        // update.js can create the variant too, so no read/add/read waterfall.
        const cart = mode === 'cap'
          ? await this.fetchCart()
          : await this.updateCart({ [this.variantId]: mode === 'ensure' ? 1 : 0 }, refresh);
        const protectionLines = this.getProtectionLines(cart);

        const keepOne = mode !== 'remove' && cart.items.some((item) =>
          item.requires_shipping && Number(item.variant_id || item.id) !== this.variantId
        );
        const updates = {};

        protectionLines.forEach((item, index) => {
          const quantity = keepOne && index === 0 ? 1 : 0;
          if (item.quantity !== quantity) updates[item.key] = quantity;
        });

        if (Object.keys(updates).length === 0) return cart;

        return this.updateCart(updates, refresh);
      }

      getProtectionLines(cart) {
        if (!Array.isArray(cart?.items)) return [];

        return cart.items.filter((item) => Number(item.variant_id || item.id) === this.variantId);
      }

      async fetchCart() {
        const response = await fetch(`${window.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          signal: window.AbortSignal?.timeout?.(15000),
        });

        return this.parseResponse(response);
      }

      async updateCart(updates, refresh = false) {
        const body = { updates };
        if (refresh) {
          body.sections = this.getCartSurfaces().map((surface) => surface.section);
          body.sections_url = window.location.pathname + window.location.search;
        }
        const response = await fetch(window.routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify(body),
          signal: window.AbortSignal?.timeout?.(15000),
        });

        return this.parseResponse(response);
      }

      async parseResponse(response) {
        const parsedState = await response.json();

        if (!response.ok || parsedState.status || parsedState.errors) throw parsedState;

        return parsedState;
      }

      /* ---- Submit the real checkout form ---------------------------------- */

      continueCheckout() {
        // Disabled submitters are omitted from form data, including name=checkout.
        if (this.secureButton) this.secureButton.disabled = false;
        const form = this.formId ? document.getElementById(this.formId) : null;
        if (!form) {
          window.location.href = '/checkout';
          return;
        }

        if (typeof form.requestSubmit === 'function' && this.secureButton && this.secureButton.form === form) {
          try {
            form.requestSubmit(this.secureButton);
            return;
          } catch (error) {
            console.error(error);
          }
        }

        if (!form.querySelector('input[name="checkout"][data-secure-fallback]')) {
          const checkoutInput = document.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = 'checkout';
          checkoutInput.value = '1';
          checkoutInput.dataset.secureFallback = 'true';
          form.appendChild(checkoutInput);
        }

        form.submit();
      }

      /* ---- State helpers -------------------------------------------------- */

      forEachSurface(callback) {
        // During customElements.define(), later instances have not upgraded yet.
        document.querySelectorAll('secure-checkout').forEach((surface) => {
          if (surface instanceof SecureCheckout) callback(surface);
        });
      }

      setCartLoading(isLoading) {
        this.forEachSurface((surface) => surface.setLoading(isLoading, true));
        if (!isLoading && this.focusedProtectionForm) {
          this.forEachSurface((surface) => {
            if (surface.formId === this.focusedProtectionForm) surface.switchButton?.focus({ preventScroll: true });
          });
          this.focusedProtectionForm = null;
        }
      }

      setLoading(isLoading, quiet = false) {
        this.isLoading = isLoading;
        this.classList.toggle('is-loading', isLoading && !quiet);
        this.toggleAttribute('aria-busy', isLoading);
        if (isLoading) this.setAttribute('aria-busy', 'true');
        [this.secureButton, this.plainButton, this.switchButton].forEach((button) => {
          if (button) button.disabled = isLoading;
        });
        if (this.switchButton) {
          if (isLoading) this.switchButton.setAttribute('aria-busy', 'true');
          else this.switchButton.removeAttribute('aria-busy');
        }
        this.querySelectorAll('.loading__spinner').forEach((spinner) =>
          spinner.classList.toggle('hidden', !isLoading || quiet)
        );
      }

      clearError() {
        if (!this.errorElement) return;
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }

      displayError() {
        const message = this.dataset.errorMessage || window.cartStrings?.error || 'Something went wrong. Please try again.';
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }
    }
  );
}
